import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../utils/api";
import DataTable from "../components/DataTable";
import { useAuth } from "../contexts/AuthContext";

const ClientInvestments = () => {
  const { userId } = useParams();
  const [client, setClient] = useState(null);
  const [investments, setInvestments] = useState([]);
  const navigate = useNavigate();
  const { formatDate } = useAuth();

  const [collapsed, setCollapsed] = useState({
    completed: true,
    deleted: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientRes = await api.get(`/admin/clients/${userId}`);
        const investmentsRes = await api.get(
          `/admin/clients/${userId}/investments`
        );
        // console.log("clientRes", clientRes.data.data.profilePicture);
        setClient(clientRes.data.data);
        setInvestments(investmentsRes.data.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [userId]);

  const statusOrder = { active: 0, completed: 1, deleted: 2 };

  const sortedInvestments = [...investments].sort((a, b) => {
    return (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
  });

  const grouped = {
    active: [],
    completed: [],
    deleted: [],
  };

  sortedInvestments.forEach((inv) => {
    if (grouped[inv.status]) {
      grouped[inv.status].push(inv);
    }
  });

  const columns = [
    {
      key: "startDate",
      header: "Start Date",
      render: (row) => formatDate(row.startDate),
    },
    { key: "fdNumber", header: "FD Number" },
    {
      key: "amount",
      header: "Amount",
      render: (row) => `₹${row.amount.toLocaleString("en-IN")}`,
    },
    {
      key: "interestRate",
      header: "Interest Rate",
      render: (row) => `${row.interestRate}%`,
    },
    { key: "duration", header: "Duration (Months)" },
    {
      key: "status",
      header: "Status",
      render: (row) => row.status.charAt(0).toUpperCase() + row.status.slice(1),
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <button
          onClick={() =>
            navigate(`/admin/clients/${userId}/investments/${row.fdNumber}`)
          }
          className="btn btn-secondary btn-sm"
        >
          Details
        </button>
      ),
    },
  ];

  const renderCollapsibleSection = (label, data, key) => {
    if (data.length === 0) return null;
    const isCollapsed = collapsed[key];
    return (
      <div className="mb-4" key={key}>
        <button
          className="btn btn-outline-primary btn-sm d-flex align-items-center"
          onClick={() => setCollapsed({ ...collapsed, [key]: !isCollapsed })}
        >
          <span className="me-1">{label} Investments</span>
          <span
            style={{
              display: "inline-block",
              transition: "transform 0.4s ease",
              transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
            }}
          >
            ▶
          </span>
        </button>
        {!isCollapsed && (
          <div className="mt-2">
            <DataTable
              columns={columns}
              data={data}
              emptyMessage="No investments found"
            />
          </div>
        )}
      </div>
    );
  };

  const renderSection = (label, data) =>
    data.length > 0 && (
      <div className="mb-4">
        <span className="mb-3 btn btn-outline-primary btn-sm">
          {label} Investments
        </span>
        <DataTable
          columns={columns}
          data={data}
          emptyMessage="No investments found"
        />
      </div>
    );

  return (
    <div className="card main-content">
      <div className="card-header flex justify-between flex-wrap mb-4">
        <div className="flex gap-3 items-center mb-4">
          <img
            src={
              client?.profilePicture ? client.profilePicture : "/default.png"
            }
            alt="Profile"
            className="profile-pic"
          />
          <h3 className="mb-0">{client?.name} → All Investments</h3>
        </div>
        <Link
          to={`/admin/clients/${userId}/investments/add`}
          className="btn btn-success btn-sm"
        >
          Add New Investment
        </Link>
      </div>
      <div className="card-body">
        {renderSection("Active", grouped.active)}
        {renderCollapsibleSection("Completed", grouped.completed, "completed")}
        {renderCollapsibleSection("Deleted", grouped.deleted, "deleted")}
      </div>
      <div className="card-footer">
        <Link to="/admin/dashboard" className="btn btn-primary btn-sm">
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default ClientInvestments;
